import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { useToast } from '@/shared/hooks/useToast';
import i18n from '@/core/i18n/config';
import { AI_APPLY_ERROR_MAP, AI_PROPOSE_ERROR_MAP } from '../errors';
import { resolveAiError } from '../errors.resolve';
import { useAiQuickCreateStore } from './useAiQuickCreateStore';
import { useApplyQuickTask } from './useApplyQuickTask';
import { useBoardContext } from './useBoardContext';
import { useQuickTaskProposal } from './useQuickTaskProposal';
import { useVoiceDictation, VOICE_ERROR_KEY } from './useVoiceDictation';
import type { QuickTaskDraftFormValues } from '../schemas';

export const RETRYABLE_ERROR_KEY = 'ai:errors.retryable';

/**
 * All state and handlers behind `AiQuickCreatePanel`: board context, the
 * per-board-scoped instruction/proposal store, voice dictation and the
 * propose/apply mutations. Propose never writes (QTC-1); confirm sends the
 * edited draft (QTC-6).
 */
export function useAiQuickCreatePanel(projectId: string, boardId: string, onClose: () => void) {
  const { t } = useTranslation('ai');
  const { toast } = useToast();
  const { boardContext, options, isLoading } = useBoardContext(projectId, boardId);
  const proposalMutation = useQuickTaskProposal(projectId, boardId);
  const applyMutation = useApplyQuickTask(projectId, boardId);

  const {
    scope: storedScope,
    instruction: storedInstruction,
    proposal: storedProposal,
    proposeErrorKey: storedProposeErrorKey,
    applyErrorKey: storedApplyErrorKey,
    enterScope,
    setInstruction,
    setProposal,
    setProposeErrorKey,
    setApplyErrorKey,
    clearErrors,
    clearDraft,
  } = useAiQuickCreateStore();

  // The store outlives the panel so the instruction survives closing it, but a proposal
  // names ids that exist on one board only. Read through the scope so nothing from
  // another board is ever rendered — not even for the frame before the effect below runs.
  const scope = `${projectId}:${boardId}`;
  const isCurrentScope = storedScope === scope;
  const instruction = isCurrentScope ? storedInstruction : '';
  const proposal = isCurrentScope ? storedProposal : null;
  const proposeErrorKey = isCurrentScope ? storedProposeErrorKey : null;
  const applyErrorKey = isCurrentScope ? storedApplyErrorKey : null;

  useEffect(() => {
    enterScope(scope);
  }, [scope, enterScope]);

  const voiceDictation = useVoiceDictation({
    onTranscription: (text) => setInstruction(text),
    onError: (error) => {
      toast.error({ title: t(VOICE_ERROR_KEY[error]) });
    },
    language: i18n.language,
  });

  const {
    confirm,
    isOpen: confirmOpen,
    options: confirmOptions,
    handleConfirm,
    handleCancel,
  } = useConfirm();

  // The instruction that produced the proposal on screen, so the panel can tell a
  // matching pair from an edited one.
  const [proposedFrom, setProposedFrom] = useState<string | null>(null);

  const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const errorKey = proposeErrorKey ?? applyErrorKey;
  const isProposePending = proposalMutation.isPending || isLoading;
  // Disabled, never hidden, while the proposal on screen still matches what was typed:
  // there is nothing to generate, but a control that vanishes reads as a bug. Editing the
  // instruction re-enables it, which is also how regenerating stays reachable.
  const hasFreshProposal = proposal !== null && instruction === proposedFrom;
  const canPropose = instruction.trim().length > 0 && !isProposePending && !hasFreshProposal;

  /**
   * Generating again replaces the proposal on screen, and with it any field the user
   * already corrected in the preview — so it asks first once there is something to lose.
   * The panel cannot see whether the preview form is dirty (that state belongs to the
   * form inside it), so the presence of a proposal is the trigger; lifting the dirty flag
   * would mean an effect syncing child state upward for a confirmation the user can
   * dismiss with one key.
   */
  async function handleGenerate() {
    if (proposal) {
      const { confirmed } = await confirm({
        title: t('quickCreate.confirmRegenerate.title'),
        description: t('quickCreate.confirmRegenerate.description'),
        confirmLabel: t('quickCreate.confirmRegenerate.confirm'),
        cancelLabel: t('quickCreate.confirmRegenerate.cancel'),
      });
      if (!confirmed) return;
    }

    runPropose();
  }

  function runPropose() {
    clearErrors();
    proposalMutation.mutate(
      { instruction, boardContext, timeZoneId },
      {
        onSuccess: (result) => {
          setProposal(result);
          setProposedFrom(instruction);
          toast.success({ title: t('quickCreate.toasts.proposalGenerated') });
        },
        onError: (error) => {
          setProposeErrorKey(resolveAiError(AI_PROPOSE_ERROR_MAP, 'POST', error));
        },
      },
    );
  }

  function handleApply(draft: QuickTaskDraftFormValues, isEdited: boolean) {
    if (!proposal) return;
    setApplyErrorKey(null);
    applyMutation.mutate(
      { draft, proposal, isEdited },
      {
        onSuccess: () => {
          // The store outlives the panel on purpose, so a cancelled draft survives closing
          // it. A created task is the one case where nothing should survive: reopening on
          // a stale proposal invites confirming the same task twice.
          clearDraft();
          setProposedFrom(null);
          const statusId = proposal.fields.statusId;
          if (statusId?.isNew && statusId.value) {
            toast.success({
              title: t('quickCreate.toasts.taskCreatedWithNewStatus', { status: statusId.value }),
            });
          } else {
            toast.success({ title: t('quickCreate.toasts.taskCreated') });
          }
          onClose();
        },
        onError: (error) => {
          setApplyErrorKey(resolveAiError(AI_APPLY_ERROR_MAP, 'POST', error));
        },
      },
    );
  }

  return {
    instruction,
    setInstruction,
    proposal,
    options,
    errorKey,
    proposeErrorKey,
    canPropose,
    isProposePending,
    voiceDictation,
    confirmOpen,
    confirmOptions,
    handleConfirm,
    handleCancel,
    handleGenerate,
    runPropose,
    handleApply,
    isApplyPending: applyMutation.isPending,
  };
}
